/**
 * Can a product be a line on an FF&E schedule? (#3906) Most of the catalog's
 * `accessory` productType is board dressing — an olive branch, a pebble, a
 * length of rope — not something a designer sources and orders.
 *
 * Dependency-free, like its sibling `addedMessage.ts`: the board chunk imports
 * it, so a stray `ui` import would drag the library page's module graph in.
 * The four category strings are spelled out rather than imported — their
 * source of truth is `CLIPPER_CATEGORY_OPTIONS` in ExpressApp's `schemas.ts`,
 * and ts-interfaces' `MainCategory` holds material categories only.
 */

/** Real brand goods a designer orders: a vase, a mirror, a pillow, a frame. */
const SPECIFIABLE_ACCESSORY_CATEGORIES: ReadonlySet<string> = new Set([
  "room decor",
  "home goods",
]);

/** Board decoration — blocked even when a brand is attached. */
const DECORATIVE_ACCESSORY_CATEGORIES: ReadonlySet<string> = new Set([
  "organic",
  "accent",
]);

/**
 * Supplier values meaning "no brand behind this". Mirrors the non-brand
 * exclusion in `library_search_mv.sql`, widened with the junk values
 * `Home/library/facetValueCleanup.ts` already denylists.
 */
const NON_SUPPLIERS: ReadonlySet<string> = new Set([
  "",
  "no supplier",
  "none",
  "null",
  "undefined",
  "n/a",
  "na",
  "-",
]);

// `\s` covers NBSP (U+00A0): productsV2.supplier carries NBSP variants of real
// values that render identically to their space-separated twins.
const normalize = (value: unknown): string =>
  typeof value === "string"
    ? value.replace(/\s+/g, " ").trim().toLowerCase()
    : "";

/** Satisfied as-is by ProductInterface, DestinationItem and a Material Sheet row. */
export interface ScheduleEligibilityFields {
  productType?: string | null;
  supplier?: string | null;
  mainCategory?: string | string[] | null;
  metadata?: { mainCategory?: string | string[] | null } | null;
}

const mainCategoryOf = (product: ScheduleEligibilityFields): string => {
  const raw = product?.metadata?.mainCategory ?? product?.mainCategory;
  return normalize(Array.isArray(raw) ? raw[0] : raw);
};

/**
 * True when this product is board decoration and must NOT be added to a
 * schedule. Gates NEW adds only — rows already on a schedule are left alone.
 *
 * ORDER IS LOAD-BEARING. "Organic" is ALSO a material main category
 * (`NewMaterialCategory.ORGANIC`), so the productType check must come first
 * or branded organic materials — cork, bamboo, jute — get blocked along with
 * the olive branches.
 *
 * The supplier fallback is not belt-and-braces: untagged accessories really
 * exist, because the clipper/MetaSku extractor only backfills a missing
 * category for materials and the legacy CG props predate the taxonomy. A real
 * supplier means a real product someone can order.
 */
export const isDecorativeAccessory = (
  product: ScheduleEligibilityFields | null | undefined,
): boolean => {
  if (!product || normalize(product.productType) !== "accessory") return false;
  const category = mainCategoryOf(product);
  if (SPECIFIABLE_ACCESSORY_CATEGORIES.has(category)) return false;
  if (DECORATIVE_ACCESSORY_CATEGORIES.has(category)) return true;
  return NON_SUPPLIERS.has(normalize(product.supplier));
};

/**
 * Did an add fail because the product is board decoration? Standalone because
 * `strict: false` means `if (!res.ok)` never narrows, so callers can't reach
 * `reason` themselves — and NOT in `ffeSchedulesApi`, which most schedule
 * tests replace wholesale with a module mock.
 */
export const isDecorativeRefusal = (result: {
  ok: boolean;
  reason?: "duplicate" | "decorative";
}): boolean => !result.ok && result.reason === "decorative";
